/**
 * @fileoverview 乗組員 個人詳細ドロワー（人材管理・ユーザー管理 共用）
 * @description SmartHR等のタレントマネジメントに倣ったタブ型プロフィール。
 *              「プロフィール / 乗船履歴 / 労務・書類」の3タブで、写真・基本情報・免状/手帳・
 *              配乗上の注意（備考）・乗船履歴（船ごと集約）・手動履歴・有給/勤怠/安全・書類を扱う。
 *              人材管理ページとユーザー管理ページの両方から userId 指定で開ける。
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Ship, Anchor, Award, AlertTriangle, MapPin, Calendar, Mail,
  ChevronDown, ChevronRight, X, Plus, Pencil, Trash2, FileText,
  History, NotebookPen, IdCard, Printer, Palmtree, Clock, Briefcase,
  CheckCircle, Circle, Save,
} from 'lucide-react';
import * as api from '../services/api';
import AvatarEditor from './AvatarEditor';
import CrewCertificatePanel from './CrewCertificatePanel';
import { hasFeature } from '../utils/featureFlags';
import PasswordReveal, { canManagePasswords } from './PasswordReveal';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';

// ── 共有型 ────────────────────────────────────────────────────────────────
export interface CurrentAssign {
  vessel_id: string; vessel_name: string; position: string | null;
  boarded_at: string | null; planned_departure_at?: string | null; boarding_days?: number | null;
}
export interface VesselSummary {
  vessel_id: string;
  vessel_name: string;
  times: number;
  total_days: number;
  first_boarded_at: string | null;
  last_at: string | null;
  on_board: boolean;
  positions: string[];
}
export interface Period {
  vessel_id: string; vessel_name: string;
  boarded_at: string | null; departed_at: string | null;
  position: string | null; days: number | null;
}
export interface ExternalEntry {
  id: string; vessel_name: string; company_name: string | null; position: string | null;
  boarded_at: string | null; departed_at: string | null; note: string | null; days: number | null;
}
export interface TalentProfile {
  id: string;
  employee_code: string;
  user_name: string;
  name_kana: string | null;
  email: string | null;
  user_role: string;
  avatar_path: string | null;
  birth_date: string | null;
  address: string | null;
  nationality: string | null;
  birth_place: string | null;
  license_no: string | null;
  license_grade: string | null;
  license_expiry_date: string | null;
  seaman_book_no: string | null;
  seaman_book_expiry_date: string | null;
  crew_note: string | null;
  created_at: string;
  current?: CurrentAssign | null;
  scheduled?: CurrentAssign | null;
  license_pdf_path?: string | null;
  license_image_path?: string | null;
  license_pdf_name?: string | null;
  license_image_name?: string | null;
  seaman_book_pdf_path?: string | null;
  seaman_book_image_path?: string | null;
  seaman_book_pdf_name?: string | null;
  seaman_book_image_name?: string | null;
}
export interface TalentLabor {
  paid_leave: { fiscal_year: string; granted_days: number; used_days: number; remaining_days: number } | null;
  work_90d: { sessions: number; work_minutes: number; overtime_minutes: number };
  incidents_1y: number;
}
export interface TalentDetail {
  user: TalentProfile;
  periods: Period[];
  vessels: VesselSummary[];
  total_days: number;
  external: ExternalEntry[];
  external_total_days: number;
  last_departed_at?: string | null;
  labor?: TalentLabor;
}

// ── 共有ヘルパー ──────────────────────────────────────────────────────────
export const fmtDate = (v?: string | null): string => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 10);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
};

/** 生年月日から満年齢 */
export function calcAge(birth?: string | null): number | null {
  if (!birth) return null;
  const b = new Date(birth);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  if (now.getMonth() < b.getMonth() || (now.getMonth() === b.getMonth() && now.getDate() < b.getDate())) age--;
  return age;
}

/** 通算日数を「N年Mヶ月」表記に */
export function fmtDuration(days: number): string {
  if (days <= 0) return '0日';
  if (days < 60) return `${days}日`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30.4);
  if (years > 0) return months > 0 ? `${years}年${months}ヶ月` : `${years}年`;
  return `${Math.floor(days / 30.4)}ヶ月`;
}

// ── 会社別の警告しきい値（roster/detail API のレスポンスで上書きされる） ──
let LICENSE_WARN_DAYS = 90;   // 免状・手帳の期限警告（日）
let LONG_BOARDING_DAYS = 60;  // 長期乗船の警告（日）

/** APIレスポンスの thresholds を反映（会社ごとの警告基準に切り替え） */
export function setTalentThresholds(t?: { license_expiry_warn_days?: number; long_boarding_warn_days?: number } | null): void {
  if (t?.license_expiry_warn_days && t.license_expiry_warn_days > 0) LICENSE_WARN_DAYS = t.license_expiry_warn_days;
  if (t?.long_boarding_warn_days && t.long_boarding_warn_days > 0) LONG_BOARDING_DAYS = t.long_boarding_warn_days;
}
export function getLicenseWarnDays(): number { return LICENSE_WARN_DAYS; }
export function getLongBoardingDays(): number { return LONG_BOARDING_DAYS; }

/** 免状・手帳の期限判定（会社別の警告日数以内は警告） */
export function expiryInfo(date?: string | null): { level: 'none' | 'ok' | 'soon' | 'expired'; days: number } {
  if (!date) return { level: 'none', days: 0 };
  const d = new Date(date);
  if (isNaN(d.getTime())) return { level: 'none', days: 0 };
  const diff = Math.floor((d.getTime() - Date.now()) / 86400000);
  if (diff < 0) return { level: 'expired', days: diff };
  if (diff <= LICENSE_WARN_DAYS) return { level: 'soon', days: diff };
  return { level: 'ok', days: diff };
}

/** 指定日から今日までの経過日数（日付ベース、当日=0） */
export function daysSince(date?: string | null): number | null {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const dd = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const nd = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.floor((nd - dd) / 86400000));
}

/** 今日から指定日までの残日数（日付ベース、当日=0。過去は負値） */
export function daysUntil(date?: string | null): number | null {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const dd = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const nd = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((dd - nd) / 86400000);
}

/** 認証付きヘッダー（PDF・書類ダウンロード等の raw fetch 用） */
function authHeaders(): Record<string, string> {
  const subdomain = (() => {
    const h = window.location.hostname;
    if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; }
    return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
  })();
  return { Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' };
}

/** 保存済み書類（免状・手帳のPDF/画像）を認証付きで開く */
function viewStoredDoc(userId: string, docType: string) {
  fetch(`${API_BASE}/licenses/${userId}/${docType}`, { headers: authHeaders() })
    .then(r => r.ok ? r.blob() : Promise.reject())
    .then(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = `${docType}_${userId}.${docType.includes('pdf') ? 'pdf' : 'jpg'}`;
      a.target = '_blank'; a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => alert('書類の表示に失敗しました'));
}

// ── 共有UI部品 ────────────────────────────────────────────────────────────
/** 汎用ピル */
export const Pill: React.FC<{ bg: string; fg: string; border?: string; children: React.ReactNode; title?: string }> = ({ bg, fg, border, children, title }) => (
  <span title={title} style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999,
    fontSize: 11, fontWeight: 700, background: bg, color: fg, border: `1px solid ${border ?? 'transparent'}`,
    whiteSpace: 'nowrap', lineHeight: 1.7,
  }}>{children}</span>
);

/** 配乗状態バッジ */
export const StatusBadge: React.FC<{ row: { current?: CurrentAssign | null; scheduled?: CurrentAssign | null } }> = ({ row }) => {
  if (row.current) return <Pill bg="#f0fdf4" fg="#16a34a" border="#bbf7d0"><Ship size={11} />乗船中</Pill>;
  if (row.scheduled) return <Pill bg="#eff6ff" fg="#2563eb" border="#bfdbfe"><Calendar size={11} />乗船予定</Pill>;
  return <Pill bg="#f1f5f9" fg="#64748b" border="#e2e8f0"><Anchor size={11} />陸上</Pill>;
};

/** 期限バッジ（免状・手帳） */
export const ExpiryPill: React.FC<{ date?: string | null }> = ({ date }) => {
  const info = expiryInfo(date);
  if (info.level === 'none') return <span style={{ color: '#94a3b8', fontSize: 12 }}>未登録</span>;
  if (info.level === 'expired') return <Pill bg="#fef2f2" fg="#dc2626" border="#fecaca"><AlertTriangle size={11} />期限切れ {fmtDate(date)}</Pill>;
  if (info.level === 'soon') return <Pill bg="#fffbeb" fg="#b45309" border="#fde68a"><AlertTriangle size={11} />残{info.days}日 {fmtDate(date)}</Pill>;
  return <Pill bg="#f0fdf4" fg="#16a34a" border="#bbf7d0">{fmtDate(date)}</Pill>;
};

/** 詳細ドロワーの情報項目 */
const InfoItem: React.FC<{ label: string; children: React.ReactNode; span2?: boolean }> = ({ label, children, span2 }) => (
  <div style={{ gridColumn: span2 ? '1 / -1' : undefined }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13.5, color: '#1e293b', fontWeight: 500, wordBreak: 'break-word' }}>{children}</div>
  </div>
);

/** セクション見出し */
const SectionTitle: React.FC<{ icon: React.ReactNode; title: string; right?: React.ReactNode }> = ({ icon, title, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 10px' }}>
    <span style={{ color: '#0369a1', display: 'flex' }}>{icon}</span>
    <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>{title}</span>
    <div style={{ marginLeft: 'auto' }}>{right}</div>
  </div>
);

// ── システム外履歴の入力フォーム（追加・編集 共用） ──────────────────────
const EMPTY_EXT = { vessel_name: '', company_name: '', position: '', boarded_at: '', departed_at: '', note: '' };
const ExternalForm: React.FC<{
  initial?: ExternalEntry | null;
  saving: boolean;
  onSubmit: (v: typeof EMPTY_EXT) => void;
  onCancel: () => void;
}> = ({ initial, saving, onSubmit, onCancel }) => {
  const [v, setV] = useState({
    vessel_name: initial?.vessel_name ?? '',
    company_name: initial?.company_name ?? '',
    position: initial?.position ?? '',
    boarded_at: initial?.boarded_at?.slice(0, 10) ?? '',
    departed_at: initial?.departed_at?.slice(0, 10) ?? '',
    note: initial?.note ?? '',
  });
  const set = (k: keyof typeof EMPTY_EXT, val: string) => setV(prev => ({ ...prev, [k]: val }));
  const inputStyle: React.CSSProperties = { width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' };
  const labelStyle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3, display: 'block' };
  return (
    <div style={{ background: '#f8fafc', border: '1.5px dashed #cbd5e1', borderRadius: 10, padding: 12, marginTop: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><span style={labelStyle}>船名 <span style={{ color: '#dc2626' }}>*</span></span>
          <input style={inputStyle} value={v.vessel_name} onChange={e => set('vessel_name', e.target.value)} placeholder="例: 第三十一〇〇丸" /></div>
        <div><span style={labelStyle}>所属会社（当時）</span>
          <input style={inputStyle} value={v.company_name} onChange={e => set('company_name', e.target.value)} placeholder="例: 〇〇海運" /></div>
        <div><span style={labelStyle}>役職</span>
          <input style={inputStyle} value={v.position} onChange={e => set('position', e.target.value)} placeholder="例: 一等航海士" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><span style={labelStyle}>乗船日</span>
            <input type="date" style={inputStyle} value={v.boarded_at} onChange={e => set('boarded_at', e.target.value)} /></div>
          <div><span style={labelStyle}>下船日</span>
            <input type="date" style={inputStyle} value={v.departed_at} onChange={e => set('departed_at', e.target.value)} /></div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}><span style={labelStyle}>メモ</span>
          <input style={inputStyle} value={v.note} onChange={e => set('note', e.target.value)} placeholder="補足があれば入力" /></div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
        <button className="btn-o" style={{ padding: '6px 14px', fontSize: 12 }} onClick={onCancel}>キャンセル</button>
        <button className="btn-p" style={{ padding: '6px 14px', fontSize: 12 }} disabled={saving || !v.vessel_name.trim()}
          onClick={() => onSubmit(v)}>
          {saving ? '保存中…' : (initial ? '更新する' : '追加する')}
        </button>
      </div>
    </div>
  );
};

/** 書類の登録状況1行（労務・書類タブ）。アップロード/差し替え/削除もここから行える */
const DocRow: React.FC<{
  label: string; userId: string; docType: string;
  path?: string | null; name?: string | null; accept: string;
  onChanged: () => void;
}> = ({ label, userId, docType, path, name, accept, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_BASE}/licenses/${userId}/${docType}`, { method: 'POST', headers: authHeaders(), body: fd });
      if (!r.ok) throw new Error();
      onChanged();
    } catch { alert('アップロードに失敗しました'); }
    setBusy(false);
  };
  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/licenses/${userId}/${docType}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) throw new Error();
      setConfirmDel(false);
      onChanged();
    } catch { alert('削除に失敗しました'); }
    setBusy(false);
  };

  const smallBtn: React.CSSProperties = { fontSize: 11, borderRadius: 6, padding: '3px 10px', cursor: busy ? 'wait' : 'pointer', border: '1px solid #e2e8f0', background: '#fff', color: '#374151' };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
      {path
        ? <CheckCircle size={15} color="#16a34a" style={{ flexShrink: 0 }} />
        : <Circle size={15} color="#cbd5e1" style={{ flexShrink: 0 }} />}
      <span style={{ fontSize: 12.5, fontWeight: 600, color: path ? '#0f172a' : '#94a3b8', flex: 1, minWidth: 140 }}>{label}</span>
      {path ? (
        confirmDel ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#991b1b' }}>削除しますか？</span>
            <button disabled={busy} onClick={doDelete}
              style={{ ...smallBtn, background: '#ef4444', color: '#fff', border: 'none', fontWeight: 700 }}>
              {busy ? '削除中…' : '削除'}
            </button>
            <button disabled={busy} onClick={() => setConfirmDel(false)} style={smallBtn}>取消</button>
          </span>
        ) : (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => viewStoredDoc(userId, docType)} title={name ?? ''}
              style={{ ...smallBtn, color: '#0066cc', borderColor: '#bfdbfe', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name || '表示'}
            </button>
            <label style={{ ...smallBtn, color: '#0369a1', background: '#e0f2fe', border: 'none' }}>
              差し替え
              <input type="file" accept={accept} style={{ display: 'none' }} disabled={busy}
                onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
            </label>
            <button onClick={() => setConfirmDel(true)} disabled={busy}
              style={{ ...smallBtn, color: '#ef4444', borderColor: '#fca5a5' }}>削除</button>
          </span>
        )
      ) : (
        <label style={{ ...smallBtn, color: '#0369a1', background: '#e0f2fe', border: '1.5px dashed #7dd3fc', fontWeight: 700 }}>
          {busy ? 'アップロード中…' : '＋ ファイルを選択'}
          <input type="file" accept={accept} style={{ display: 'none' }} disabled={busy}
            onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </label>
      )}
    </div>
  );
};

// ── 個人詳細ドロワー本体 ──────────────────────────────────────────────────
type DrawerTab = 'profile' | 'history' | 'labor';

const TalentDetailDrawer: React.FC<{
  userId: string;
  onClose: () => void;
  onChanged: () => void;   // 備考・履歴・写真の変更を呼び出し元へ反映
  initialTab?: DrawerTab;
}> = ({ userId, onClose, onChanged, initialTab = 'profile' }) => {
  const [detail, setDetail] = useState<TalentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<DrawerTab>(initialTab);
  const [histView, setHistView] = useState<'byShip' | 'timeline'>('byShip');
  const [openVessels, setOpenVessels] = useState<Record<string, boolean>>({});
  const [note, setNote] = useState('');
  const [noteDirty, setNoteDirty] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [extAdding, setExtAdding] = useState(false);
  const [extEditing, setExtEditing] = useState<ExternalEntry | null>(null);
  const [extSaving, setExtSaving] = useState(false);
  const [extDeleting, setExtDeleting] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ── プロフィール編集モード（基本情報・免状・手帳をその場で編集） ──
  const EMPTY_EDIT = {
    user_name: '', name_kana: '', email: '', birth_date: '', nationality: '', birth_place: '', address: '',
    license_grade: '', license_no: '', license_expiry_date: '', seaman_book_no: '', seaman_book_expiry_date: '',
  };
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = () => {
    const u = detail?.user;
    if (!u) return;
    setEditForm({
      user_name: u.user_name ?? '', name_kana: u.name_kana ?? '', email: u.email ?? '',
      birth_date: (u.birth_date ?? '').slice(0, 10), nationality: u.nationality ?? '', birth_place: u.birth_place ?? '',
      address: u.address ?? '', license_grade: u.license_grade ?? '', license_no: u.license_no ?? '',
      license_expiry_date: (u.license_expiry_date ?? '').slice(0, 10), seaman_book_no: u.seaman_book_no ?? '',
      seaman_book_expiry_date: (u.seaman_book_expiry_date ?? '').slice(0, 10),
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!editForm.user_name.trim()) { alert('氏名は必須です'); return; }
    setEditSaving(true);
    try {
      await api.put(`/users/${userId}`, {
        user_name: editForm.user_name.trim(),
        name_kana: editForm.name_kana,
        email: editForm.email || undefined,
        birth_date: editForm.birth_date,
        nationality: editForm.nationality,
        birth_place: editForm.birth_place,
        address: editForm.address,
        license_grade: editForm.license_grade,
        license_no: editForm.license_no,
        license_expiry_date: editForm.license_expiry_date,
        seaman_book_no: editForm.seaman_book_no,
        seaman_book_expiry_date: editForm.seaman_book_expiry_date,
      });
      setEditMode(false);
      await load();
      onChanged();
    } catch (e: any) {
      alert(e?.message ?? '保存に失敗しました');
    }
    setEditSaving(false);
  };

  const editInputStyle: React.CSSProperties = {
    width: '100%', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8,
    fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fffdf0',
  };
  // ※ コンポーネントではなくプレーン関数（コンポーネント化すると再レンダーごとに
  //   input が作り直され、1文字ごとにフォーカスが外れるため）
  const editField = (label: string, k: keyof typeof EMPTY_EDIT, opts?: { type?: string; span2?: boolean; placeholder?: string }) => (
    <div style={{ gridColumn: opts?.span2 ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 3 }}>{label}</div>
      <input
        type={opts?.type ?? 'text'}
        value={editForm[k]}
        placeholder={opts?.placeholder}
        onChange={e => setEditForm(f => ({ ...f, [k]: e.target.value }))}
        style={editInputStyle}
      />
    </div>
  );

  const load = useCallback(async () => {
    try {
      const res = await api.get<any>(`/talent/${userId}`);
      setTalentThresholds(res.thresholds);
      const d = res.data as TalentDetail;
      setDetail(d);
      setNote(d.user.crew_note ?? '');
      setNoteDirty(false);
    } catch (e) { console.error('[Talent/detail]', e); }
    setLoading(false);
  }, [userId]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  // ESC で閉じる
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      await api.put(`/talent/${userId}/note`, { crew_note: note });
      setNoteDirty(false);
      onChanged();
    } catch { alert('備考の保存に失敗しました'); }
    setNoteSaving(false);
  };

  const submitExternal = async (v: typeof EMPTY_EXT) => {
    setExtSaving(true);
    const body = {
      vessel_name: v.vessel_name, company_name: v.company_name, position: v.position,
      boarded_at: v.boarded_at, departed_at: v.departed_at, note: v.note,
    };
    try {
      if (extEditing) await api.put(`/talent/external-history/${extEditing.id}`, body);
      else await api.post(`/talent/${userId}/external-history`, body);
      setExtAdding(false); setExtEditing(null);
      await load();
      onChanged();
    } catch (e: any) { alert(e?.message ?? '保存に失敗しました'); }
    setExtSaving(false);
  };

  const deleteExternal = async (entry: ExternalEntry) => {
    if (!window.confirm(`「${entry.vessel_name}」の手動履歴を削除しますか？`)) return;
    setExtDeleting(entry.id);
    try {
      await api.del(`/talent/external-history/${entry.id}`);
      await load();
      onChanged();
    } catch { alert('削除に失敗しました'); }
    setExtDeleting(null);
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const r = await fetch(`${API_BASE}/boarding/pdf?user_id=${encodeURIComponent(userId)}`, { headers: authHeaders() });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `乗船履歴_${detail?.user.user_name ?? userId}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { alert('PDF出力に失敗しました'); }
    setPdfLoading(false);
  };

  const u = detail?.user;
  const labor = detail?.labor;
  const age = calcAge(u?.birth_date);
  const grandTotal = (detail?.total_days ?? 0) + (detail?.external_total_days ?? 0);
  const ashore = u && !u.current ? daysSince(detail?.last_departed_at ?? null) : null;
  const departIn = daysUntil(u?.current?.planned_departure_at ?? null);

  const TABS: { key: DrawerTab; icon: React.ReactNode; label: string }[] = [
    { key: 'profile', icon: <IdCard size={14} />, label: 'プロフィール' },
    { key: 'history', icon: <History size={14} />, label: '乗船履歴' },
    { key: 'labor', icon: <Briefcase size={14} />, label: '労務・書類' },
  ];

  return (
    <>
      {/* 背景オーバーレイ */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 900 }} />
      {/* ドロワー本体 */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(660px, 94vw)', zIndex: 901,
        background: '#fff', boxShadow: '-12px 0 40px rgba(15,23,42,0.18)',
        display: 'flex', flexDirection: 'column', animation: 'talentDrawerIn .22s ease',
      }}>
        <style>{`@keyframes talentDrawerIn { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>

        {/* ヘッダー */}
        <div style={{ padding: '18px 24px 0', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #f8fafc 0%, #f0f9ff 100%)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {u && (
              <AvatarEditor entityId={u.id} type="user" imagePath={u.avatar_path} displayName={u.user_name} size={72}
                onUpdated={() => { onChanged(); }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              {u ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{u.user_name}</span>
                    {u.name_kana && <span style={{ fontSize: 12, color: '#64748b' }}>{u.name_kana}</span>}
                    {age != null && <span style={{ fontSize: 12, color: '#64748b' }}>{age}歳</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    <Pill bg="#f1f5f9" fg="#475569" border="#e2e8f0">{u.employee_code}</Pill>
                    <StatusBadge row={u} />
                    {u.current?.vessel_name && (
                      <Pill bg="#ecfeff" fg="#0e7490" border="#a5f3fc"><Ship size={11} />{u.current.vessel_name}{u.current.position ? ` / ${u.current.position}` : ''}</Pill>
                    )}
                    {u.current?.boarding_days != null && (
                      <Pill bg="#f0fdf4" fg="#15803d" border="#bbf7d0">乗船{u.current.boarding_days}日目</Pill>
                    )}
                    {departIn != null && departIn >= 0 && (
                      <Pill bg={departIn <= 14 ? '#fffbeb' : '#f8fafc'} fg={departIn <= 14 ? '#b45309' : '#64748b'} border={departIn <= 14 ? '#fde68a' : '#e2e8f0'}>
                        下船予定 {fmtDate(u.current?.planned_departure_at)}（あと{departIn}日）
                      </Pill>
                    )}
                    {!u.current && ashore != null && (
                      <Pill bg="#eff6ff" fg="#1d4ed8" border="#bfdbfe"><Anchor size={11} />陸上{ashore}日</Pill>
                    )}
                    {!u.current && u.scheduled?.vessel_name && (
                      <Pill bg="#eff6ff" fg="#2563eb" border="#bfdbfe"><Calendar size={11} />{u.scheduled.vessel_name} 乗船予定</Pill>
                    )}
                  </div>
                </>
              ) : <span style={{ color: '#94a3b8' }}>読み込み中…</span>}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 6, alignSelf: 'flex-start' }}>
              <X size={20} />
            </button>
          </div>

          {/* タブバー */}
          <div style={{ display: 'flex', gap: 2, marginTop: 14 }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  border: 'none', background: 'transparent',
                  color: tab === t.key ? '#0369a1' : '#64748b',
                  borderBottom: tab === t.key ? '2.5px solid #0369a1' : '2.5px solid transparent',
                  marginBottom: -1,
                }}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>
        </div>

        {/* ボディ */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 24px 28px' }}>
          {loading && <div className="empty" style={{ padding: 40 }}>読み込み中…</div>}
          {!loading && detail && u && (
            <>
              {/* ═══ タブ1: プロフィール ═══ */}
              {tab === 'profile' && (
                <>
                  <SectionTitle icon={<IdCard size={15} />} title="基本情報"
                    right={editMode ? (
                      <span style={{ display: 'inline-flex', gap: 8 }}>
                        <button className="btn-o" style={{ padding: '5px 14px', fontSize: 12 }} disabled={editSaving}
                          onClick={() => setEditMode(false)}>キャンセル</button>
                        <button className="btn-p" style={{ padding: '5px 14px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                          disabled={editSaving} onClick={saveEdit}>
                          <Save size={13} />{editSaving ? '保存中…' : '保存'}
                        </button>
                      </span>
                    ) : (
                      <button className="btn-o" style={{ padding: '5px 14px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        onClick={openEdit}>
                        <Pencil size={13} />編集
                      </button>
                    )} />
                  {editMode ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', background: '#fff', border: '1.5px solid #bae6fd', borderRadius: 12, padding: 16 }}>
                      {editField('氏名 *', 'user_name')}
                      {editField('ふりがな', 'name_kana', { placeholder: '例: やまだ たろう' })}
                      {editField('生年月日', 'birth_date', { type: 'date' })}
                      {editField('メール', 'email', { placeholder: 'example@email.com' })}
                      {editField('国籍', 'nationality', { placeholder: '例: 日本' })}
                      {editField('出身地', 'birth_place', { placeholder: '例: 香川県' })}
                      {editField('住所', 'address', { span2: true, placeholder: '例: 香川県三豊市…' })}
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                      <InfoItem label="生年月日">{u.birth_date ? `${fmtDate(u.birth_date)}${age != null ? `（${age}歳）` : ''}` : '-'}</InfoItem>
                      <InfoItem label="国籍 / 出身地">{[u.nationality, u.birth_place].filter(Boolean).join(' / ') || '-'}</InfoItem>
                      <InfoItem label="住所" span2>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <MapPin size={13} color="#94a3b8" style={{ flexShrink: 0 }} />{u.address || '-'}
                        </span>
                      </InfoItem>
                      <InfoItem label="メール" span2>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Mail size={13} color="#94a3b8" style={{ flexShrink: 0 }} />{u.email || '-'}
                        </span>
                      </InfoItem>
                    </div>
                  )}

                  {/* ログイン情報（陸上管理者・システム管理者・運営管理者だけに出す） */}
                  {canManagePasswords() && (
                    <>
                      <SectionTitle icon={<IdCard size={15} />} title="ログイン情報" />
                      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
                        <PasswordReveal userId={u.id} targetRole={u.user_role} employeeCode={u.employee_code}
                          hint="変更するときは、ユーザー管理の「編集」で新しいパスワードを設定してください。" />
                      </div>
                    </>
                  )}

                  <SectionTitle icon={<Award size={15} />} title="海技免状・船員手帳" />
                  {editMode ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', background: '#fff', border: '1.5px solid #bae6fd', borderRadius: 12, padding: 16 }}>
                      {editField('海技免状の種類', 'license_grade', { placeholder: '例: 四級海技士(航海)' })}
                      {editField('免状番号', 'license_no', { placeholder: '例: 第○○号' })}
                      {editField('免状有効期限', 'license_expiry_date', { type: 'date' })}
                      {editField('船員手帳 有効期限', 'seaman_book_expiry_date', { type: 'date' })}
                      {editField('船員手帳番号', 'seaman_book_no', { span2: true, placeholder: '例: ○○ 第○○号' })}
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>海技免状</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>{u.license_grade || '未登録'}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{u.license_no ? `番号: ${u.license_no}` : ''}</div>
                        <ExpiryPill date={u.license_expiry_date} />
                      </div>
                      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 4 }}>船員手帳</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>{u.seaman_book_no || '未登録'}</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{u.seaman_book_no ? '手帳番号' : ''}</div>
                        <ExpiryPill date={u.seaman_book_expiry_date} />
                      </div>
                    </div>
                  )}

                  <SectionTitle icon={<NotebookPen size={15} />} title="配乗上の注意（備考）"
                    right={noteDirty && (
                      <button className="btn-p" style={{ padding: '5px 14px', fontSize: 12 }} disabled={noteSaving} onClick={saveNote}>
                        {noteSaving ? '保存中…' : '保存'}
                      </button>
                    )} />
                  <div style={{ background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 12, padding: 12 }}>
                    <textarea
                      value={note}
                      onChange={e => { setNote(e.target.value); setNoteDirty(true); }}
                      placeholder="例: 〇〇氏と同船不可 / 機関経験が浅いためベテランと組ませる / 持病あり・服薬中 など"
                      rows={3}
                      style={{ width: '100%', border: 'none', background: 'transparent', resize: 'vertical', fontSize: 13, color: '#78350f', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.7 }}
                    />
                    <div style={{ fontSize: 10.5, color: '#b45309', marginTop: 4 }}>※ 管理者のみ閲覧できます。配乗計画時の参考情報として活用してください。</div>
                  </div>
                </>
              )}

              {/* ═══ タブ2: 乗船履歴 ═══ */}
              {tab === 'history' && (
                <>
                  {/* 通算サマリー */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 16 }}>
                    {[
                      { label: '通算乗船', value: fmtDuration(grandTotal), sub: `${grandTotal.toLocaleString()}日` },
                      { label: '乗船回数', value: `${detail.periods.length + detail.external.length}回`, sub: `手動 ${detail.external.length}回含む` },
                      { label: '経験船舶', value: `${detail.vessels.length + new Set(detail.external.map(e => e.vessel_name)).size}隻`, sub: `自社 ${detail.vessels.length}隻` },
                    ].map(s => (
                      <div key={s.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>{s.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0369a1', lineHeight: 1.3 }}>{s.value}</div>
                        <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{s.sub}</div>
                      </div>
                    ))}
                  </div>

                  <SectionTitle icon={<History size={15} />} title="乗船履歴"
                    right={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {/* 表示切替: 船別集約 / 時系列ヒストリー */}
                        <span style={{ display: 'inline-flex', background: '#f1f5f9', borderRadius: 8, padding: 2 }}>
                          {([['byShip', '船別'], ['timeline', '時系列']] as ['byShip' | 'timeline', string][]).map(([m, label]) => (
                            <button key={m} onClick={() => setHistView(m)}
                              style={{
                                padding: '4px 12px', borderRadius: 6, border: 'none', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                                background: histView === m ? '#fff' : 'transparent',
                                color: histView === m ? '#0369a1' : '#64748b',
                                boxShadow: histView === m ? '0 1px 3px rgba(15,23,42,0.12)' : 'none',
                              }}>{label}</button>
                          ))}
                        </span>
                        <button className="btn-o" style={{ padding: '5px 12px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                          disabled={pdfLoading} onClick={downloadPdf}>
                          <Printer size={13} />{pdfLoading ? '出力中…' : '履歴PDF'}
                        </button>
                      </span>
                    } />
                  {histView === 'timeline' ? (
                    /* ── 時系列ヒストリー（CYDAS型タイムライン。手動履歴も統合表示） ── */
                    (() => {
                      interface TlEvent { date: string; kind: 'board' | 'disembark' | 'onboard' | 'external'; vessel: string; position?: string | null; days?: number | null; company?: string | null; note?: string | null }
                      const events: TlEvent[] = [];
                      for (const p of detail.periods) {
                        if (p.boarded_at) events.push({ date: p.boarded_at, kind: 'board', vessel: p.vessel_name, position: p.position });
                        if (p.departed_at) events.push({ date: p.departed_at, kind: 'disembark', vessel: p.vessel_name, position: p.position, days: p.days });
                        else if (p.boarded_at) events.push({ date: new Date().toISOString(), kind: 'onboard', vessel: p.vessel_name, position: p.position, days: p.days });
                      }
                      for (const e of detail.external) {
                        const d = e.departed_at ?? e.boarded_at;
                        if (d) events.push({ date: d, kind: 'external', vessel: e.vessel_name, position: e.position, days: e.days, company: e.company_name, note: e.note });
                      }
                      events.sort((a, b) => String(b.date).localeCompare(String(a.date)));
                      if (events.length === 0) {
                        return <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '18px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>履歴がまだありません</div>;
                      }
                      const KIND: Record<TlEvent['kind'], { color: string; bg: string; label: (e: TlEvent) => string }> = {
                        onboard:   { color: '#16a34a', bg: '#dcfce7', label: e => `${e.vessel} に乗船中` },
                        board:     { color: '#0ea5e9', bg: '#e0f2fe', label: e => `${e.vessel} に乗船` },
                        disembark: { color: '#64748b', bg: '#f1f5f9', label: e => `${e.vessel} を下船` },
                        external:  { color: '#94a3b8', bg: '#f8fafc', label: e => `${e.vessel}${e.company ? `（${e.company}）` : ''} を下船` },
                      };
                      return (
                        <div style={{ position: 'relative', paddingLeft: 20 }}>
                          {/* 縦ライン */}
                          <div style={{ position: 'absolute', left: 6, top: 8, bottom: 8, width: 2, background: '#e2e8f0', borderRadius: 2 }} />
                          {events.map((e, i) => {
                            const k = KIND[e.kind];
                            return (
                              <div key={i} style={{ position: 'relative', padding: '7px 0 7px 8px' }}>
                                <span style={{ position: 'absolute', left: -20, top: 13, width: 14, height: 14, borderRadius: '50%', background: k.bg, border: `3px solid ${k.color}`, boxSizing: 'border-box' }} />
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#94a3b8', fontVariantNumeric: 'tabular-nums', minWidth: 78 }}>
                                    {e.kind === 'onboard' ? '現在' : fmtDate(e.date)}
                                  </span>
                                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{k.label(e)}</span>
                                  {e.position && <Pill bg="#eff6ff" fg="#2563eb">{e.position}</Pill>}
                                  {e.kind !== 'board' && e.days != null && (
                                    <span style={{ fontSize: 11.5, color: '#64748b' }}>{e.kind === 'onboard' ? `${e.days}日経過` : `${e.days}日間乗船`}</span>
                                  )}
                                  {e.kind === 'external' && <Pill bg="#f8fafc" fg="#94a3b8" border="#e2e8f0">手動記録</Pill>}
                                </div>
                                {e.note && <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 2, paddingLeft: 88 }}>{e.note}</div>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                  ) : detail.vessels.length === 0 ? (
                    <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '18px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>
                      乗下船記録がまだありません
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {detail.vessels.map(vs => {
                        const open = !!openVessels[vs.vessel_id];
                        const vPeriods = detail.periods.filter(p => p.vessel_id === vs.vessel_id).slice().reverse();
                        return (
                          <div key={vs.vessel_id} style={{ border: `1.5px solid ${vs.on_board ? '#86efac' : '#e2e8f0'}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                            <button
                              onClick={() => setOpenVessels(prev => ({ ...prev, [vs.vessel_id]: !open }))}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: vs.on_board ? '#f0fdf4' : '#fafafa', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                              {open ? <ChevronDown size={15} color="#64748b" /> : <ChevronRight size={15} color="#64748b" />}
                              <Ship size={15} color={vs.on_board ? '#16a34a' : '#64748b'} />
                              <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>{vs.vessel_name || '(船名不明)'}</span>
                              {vs.on_board && <Pill bg="#dcfce7" fg="#15803d">乗船中</Pill>}
                              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {vs.times}回 ・ 計{fmtDuration(vs.total_days)}
                              </span>
                            </button>
                            {open && (
                              <div style={{ padding: '4px 14px 12px' }}>
                                {vs.positions.length > 0 && (
                                  <div style={{ fontSize: 11.5, color: '#64748b', margin: '6px 0 8px' }}>経験役職: {vs.positions.join('、')}</div>
                                )}
                                <table style={{ fontSize: 12.5 }}>
                                  <thead><tr>
                                    <th style={{ padding: '6px 8px' }}>乗船</th>
                                    <th style={{ padding: '6px 8px' }}>下船</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>日数</th>
                                    <th style={{ padding: '6px 8px' }}>役職</th>
                                  </tr></thead>
                                  <tbody>
                                    {vPeriods.map((p, i) => (
                                      <tr key={i}>
                                        <td style={{ padding: '7px 8px' }}>{fmtDate(p.boarded_at)}</td>
                                        <td style={{ padding: '7px 8px' }}>{p.departed_at ? fmtDate(p.departed_at) : <span style={{ color: '#16a34a', fontWeight: 700 }}>乗船中</span>}</td>
                                        <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700 }}>{p.days != null ? `${p.days}日` : '-'}</td>
                                        <td style={{ padding: '7px 8px' }}>{p.position || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <SectionTitle icon={<FileText size={15} />} title="システム登録前の乗船履歴（手動入力）"
                    right={!extAdding && !extEditing && (
                      <button className="btn-o" style={{ padding: '5px 12px', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}
                        onClick={() => { setExtAdding(true); setExtEditing(null); }}>
                        <Plus size={13} />追加
                      </button>
                    )} />
                  <div style={{ fontSize: 11.5, color: '#94a3b8', margin: '-4px 0 8px' }}>
                    本システム導入前や他社在籍時の経歴を記録できます（自動計算とは別枠で管理）。
                  </div>
                  {detail.external.length === 0 && !extAdding && (
                    <div style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, padding: '14px', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>
                      手動入力の履歴はありません
                    </div>
                  )}
                  {detail.external.map(entry => (
                    extEditing?.id === entry.id ? (
                      <ExternalForm key={entry.id} initial={entry} saving={extSaving} onSubmit={submitExternal} onCancel={() => setExtEditing(null)} />
                    ) : (
                      <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e2e8f0', borderLeft: '3px solid #94a3b8', borderRadius: 10, padding: '10px 12px', marginBottom: 6, background: '#fff' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{entry.vessel_name}</span>
                            {entry.company_name && <Pill bg="#f1f5f9" fg="#64748b">{entry.company_name}</Pill>}
                            {entry.position && <Pill bg="#eff6ff" fg="#2563eb">{entry.position}</Pill>}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                            {fmtDate(entry.boarded_at)} 〜 {fmtDate(entry.departed_at)}
                            {entry.days != null && <span style={{ fontWeight: 700, color: '#475569' }}>（{entry.days}日）</span>}
                            {entry.note && <span style={{ marginLeft: 8, color: '#94a3b8' }}>{entry.note}</span>}
                          </div>
                        </div>
                        <button title="編集" onClick={() => { setExtEditing(entry); setExtAdding(false); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><Pencil size={14} /></button>
                        <button title="削除" disabled={extDeleting === entry.id} onClick={() => deleteExternal(entry)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 4 }}><Trash2 size={14} /></button>
                      </div>
                    )
                  ))}
                  {extAdding && <ExternalForm saving={extSaving} onSubmit={submitExternal} onCancel={() => setExtAdding(false)} />}
                </>
              )}

              {/* ═══ タブ3: 労務・書類 ═══ */}
              {tab === 'labor' && (
                <>
                  {/* 資格・期限（機能フラグ crew_certificates の会社）: 免状・手帳・STCW・健康証明書を種別ごとに管理 */}
                  {hasFeature('crew_certificates') && (
                    <>
                      <SectionTitle icon={<Award size={15} />} title="資格・期限（免状・手帳・STCW・健康証明書）" />
                      <CrewCertificatePanel userId={u.id} compact onChanged={() => { load(); onChanged(); }} />
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                        ここで登録した海技免状・船員手帳は、プロフィールタブの免状・手帳欄（帳票・配乗シミュレーションが参照）にも自動で反映されます。
                      </div>
                    </>
                  )}
                  <SectionTitle icon={<Palmtree size={15} />} title="有給休暇" />
                  {/* 有給・休暇機能は準備中（公開時はこのブロックを labor.paid_leave 表示に戻す） */}
                  <div style={{ background: '#f8fafc', border: '1.5px dashed #cbd5e1', borderRadius: 12, padding: '18px 14px', textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '3px 12px', marginBottom: 6 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
                      <span style={{ color: '#2563eb', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em' }}>COMING SOON</span>
                    </span>
                    <div style={{ fontSize: 12.5, color: '#64748b' }}>有給・休暇管理は準備中です</div>
                  </div>

                  <SectionTitle icon={<Clock size={15} />} title="直近90日の勤務実績" />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {[
                      { label: '勤務日数', value: `${labor?.work_90d.sessions ?? 0}日`, color: '#0369a1' },
                      { label: '総労働時間', value: `${Math.round((labor?.work_90d.work_minutes ?? 0) / 60)}h`, color: '#0369a1' },
                      { label: '時間外', value: `${Math.round((labor?.work_90d.overtime_minutes ?? 0) / 60)}h`, color: (labor?.work_90d.overtime_minutes ?? 0) >= 60 * 45 ? '#dc2626' : '#0369a1' },
                    ].map(s => (
                      <div key={s.label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    詳細は <span style={{ color: '#0369a1', fontWeight: 700 }}>勤怠一覧・休息時間</span> ページで確認できます。
                  </div>

                  <SectionTitle icon={<AlertTriangle size={15} />} title="安全（ヒヤリハット）" />
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: (labor?.incidents_1y ?? 0) > 0 ? '#fef3c7' : '#f0fdf4', color: (labor?.incidents_1y ?? 0) > 0 ? '#b45309' : '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <AlertTriangle size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{labor?.incidents_1y ?? 0}件</div>
                      <div style={{ fontSize: 11.5, color: '#64748b' }}>直近1年のヒヤリハット報告（報告は安全意識の高さの表れでもあります）</div>
                    </div>
                  </div>

                  <SectionTitle icon={<FileText size={15} />} title="書類の登録状況" />
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                    <DocRow label="海技免状 PDF（スキャン原本）" userId={u.id} docType="pdf" accept=".pdf" path={u.license_pdf_path} name={u.license_pdf_name} onChanged={() => { load(); onChanged(); }} />
                    <DocRow label="海技免状 写真" userId={u.id} docType="image" accept="image/*" path={u.license_image_path} name={u.license_image_name} onChanged={() => { load(); onChanged(); }} />
                    <DocRow label="船員手帳 PDF（スキャン原本）" userId={u.id} docType="seaman-book-pdf" accept=".pdf" path={u.seaman_book_pdf_path} name={u.seaman_book_pdf_name} onChanged={() => { load(); onChanged(); }} />
                    <DocRow label="船員手帳 写真" userId={u.id} docType="seaman-book-image" accept="image/*" path={u.seaman_book_image_path} name={u.seaman_book_image_name} onChanged={() => { load(); onChanged(); }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    PDF・写真をここから直接アップロード／差し替え／削除できます（PDFは20MBまで）。
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TalentDetailDrawer;
